import { SegmentedControl } from '@mlm/ui';
import type { RpPageTheme } from '@mlm/shared';
import { hexToHslTriplet, hslTripletToHex } from '../../lib/color';
import { RpFieldRow } from './fields';

/**
 * Per-page theme overrides.
 *
 * Applied as CSS custom properties on the page's root wrapper, NOT on `<html>` —
 * so a campaign can look different from the rest of the member app without
 * touching the tenant's baked theme or leaking into other routes. Any field left
 * blank falls through to the tenant theme, which is why an un-themed page looks
 * exactly like it always did.
 */
export function RpThemePanel({
  theme,
  onChange,
}: {
  theme: RpPageTheme;
  onChange: (next: RpPageTheme) => void;
}) {
  const set = <K extends keyof RpPageTheme>(key: K, value: RpPageTheme[K]) => {
    const next = { ...theme };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Leave a colour untouched to inherit the site theme. These only affect this page.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <ColorField label="Accent" value={theme.accent} onChange={(v) => set('accent', v)} />
        <ColorField
          label="Accent text"
          value={theme.accentFg}
          onChange={(v) => set('accentFg', v)}
        />
        <ColorField
          label="Page background"
          value={theme.pageBg}
          onChange={(v) => set('pageBg', v)}
        />
        <ColorField label="Page text" value={theme.pageFg} onChange={(v) => set('pageFg', v)} />
      </div>
      <RpFieldRow label="Corners">
        <SegmentedControl
          size="sm"
          value={theme.radius ?? 'md'}
          onChange={(v) => set('radius', v)}
          options={[
            { value: 'none', label: 'Square' },
            { value: 'sm', label: 'S' },
            { value: 'md', label: 'M' },
            { value: 'lg', label: 'L' },
            { value: 'xl', label: 'Round' },
          ]}
          ariaLabel="Corner radius"
        />
      </RpFieldRow>
    </div>
  );
}

/**
 * A hex swatch over an HSL-triplet value. Colours are stored as triplets to
 * match the `ThemeTokens` convention (they feed `hsl(var(--x))`), but nobody
 * thinks in triplets — so the control is a native colour picker.
 */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  const hex = value ? hslTripletToHex(value) : '#000000';
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={hex}
          className="h-8 w-10 cursor-pointer rounded border bg-transparent p-0.5"
          onChange={(e) => onChange(hexToHslTriplet(e.target.value))}
        />
        {value ? (
          <button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-destructive"
            onClick={() => onChange(undefined)}
          >
            Reset
          </button>
        ) : (
          <span className="text-[11px] text-muted-foreground/70">Inherited</span>
        )}
      </div>
    </div>
  );
}
